import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import axiosInstance from "../axiosInstance";

// ─── File-type icon (inline SVG — same as Asset Management section) ──────────

const FILE_EXT_COLORS = {
  png:  { bg: "#ede9fe", label: "#7c3aed", border: "#c4b5fd" },
  jpg:  { bg: "#fce7f3", label: "#be185d", border: "#f9a8d4" },
  jpeg: { bg: "#fce7f3", label: "#be185d", border: "#f9a8d4" },
  gif:  { bg: "#fef3c7", label: "#b45309", border: "#fcd34d" },
  webp: { bg: "#f0fdf4", label: "#15803d", border: "#86efac" },
  svg:  { bg: "#fff7ed", label: "#c2410c", border: "#fdba74" },
  pdf:  { bg: "#fee2e2", label: "#b91c1c", border: "#fca5a5" },
  csv:  { bg: "#dcfce7", label: "#166534", border: "#86efac" },
  xlsx: { bg: "#dcfce7", label: "#166534", border: "#86efac" },
  xls:  { bg: "#dcfce7", label: "#166534", border: "#86efac" },
  docx: { bg: "#dbeafe", label: "#1d4ed8", border: "#93c5fd" },
  doc:  { bg: "#dbeafe", label: "#1d4ed8", border: "#93c5fd" },
  json: { bg: "#fff7ed", label: "#ea580c", border: "#fdba74" },
  txt:  { bg: "#f3f4f6", label: "#374151", border: "#d1d5db" },
};

const FileIcon = ({ fileName = "", size = 28 }) => {
  const ext = (fileName.includes(".") ? fileName.split(".").pop() : "").toLowerCase();
  const colors = FILE_EXT_COLORS[ext] || { bg: "#f3f4f6", label: "#6b7280", border: "#d1d5db" };
  const label = (ext ? ext.toUpperCase() : "FILE").slice(0, 4);
  const w = size;
  const h = Math.round(size * 1.3);
  return (
    <svg width={w} height={h} viewBox="0 0 40 52" fill="none" style={{ flexShrink: 0, display: "block" }}>
      <path d="M4,0 H28 L40,12 V48 Q40,52 36,52 H4 Q0,52 0,48 V4 Q0,0 4,0 Z" fill={colors.bg} stroke={colors.border} strokeWidth="2" />
      <path d="M28,0 L40,12 H30 Q28,12 28,10 Z" fill={colors.border} />
      <text x="20" y="40" textAnchor="middle" fontSize={label.length <= 3 ? "13" : "10"} fontWeight="800" fontFamily="system-ui, -apple-system, sans-serif" fill={colors.label} letterSpacing="0.3">
        {label}
      </text>
    </svg>
  );
};

// ─── Thumbnail for image files ───────────────────────────────────────────────

const AssetThumb = ({ asset, size = 28 }) => {
  const isImage = asset.contentType?.startsWith("image/");
  const h = Math.round(size * 1.3);
  if (isImage && asset.url) {
    return (
      <Box
        component="img"
        src={asset.url}
        alt={asset.name}
        style={{ width: size, height: h, objectFit: "cover", borderRadius: 4, display: "block", flexShrink: 0 }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  return <FileIcon fileName={asset.name} size={size} />;
};

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

/**
 * AssetPickerDialog
 *
 * Props:
 *   open         {boolean}           — controls visibility
 *   onClose      {() => void}        — called when dismissed without selection
 *   onSelect     {(asset) => void}   — called with the selected asset object
 *   tenantId     {string}            — tenant to load assets for
 *   title        {string}            — optional dialog title (default "Select Asset")
 *   accept       {string[]}          — optional content-type filter e.g. ["text/csv"]
 */
const AssetPickerDialog = ({
  open,
  onClose,
  onSelect,
  tenantId,
  title = "Select a Tenant Asset",
  accept,
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchAssets = useCallback(async (pageNum = 0) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/assets/${tenantId}?page=${pageNum + 1}&pageSize=${PAGE_SIZE}&visibility=public`
      );
      const data = response.data;
      setAssets(data.items || []);
      setTotalCount(data.totalCount ?? 0);
      setPage(pageNum);
    } catch {
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Reload whenever the dialog opens
  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch("");
      fetchAssets(0);
    }
  }, [open, fetchAssets]);

  const filtered = assets.filter((a) => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = !accept || accept.length === 0 || accept.includes(a.contentType);
    return matchesSearch && matchesType;
  });

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          border: "1px solid rgba(255, 132, 76, 0.12)",
          background: "linear-gradient(180deg, rgba(255,248,243,0.98), rgba(255,255,255,0.96))",
          boxShadow: "0 30px 70px rgba(236,123,55,0.16)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,132,76,0.10)",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <FolderOutlinedIcon sx={{ color: "#f97316", fontSize: 22 }} />
          <Typography sx={{ fontWeight: 700, color: "#202531", fontSize: "1rem" }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => fetchAssets(page)}
              disabled={loading}
              sx={{ color: "#9ca3af", "&:hover": { color: "#f97316" } }}
            >
              <RefreshOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#6b7280", "&:hover": { color: "#f97316" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Search */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search files…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#f97316" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "rgba(255,132,76,0.20)" },
              "&:hover fieldset": { borderColor: "#f97316" },
              "&.Mui-focused fieldset": { borderColor: "#f97316", borderWidth: "1px" },
            },
          }}
        />
      </Box>

      {/* File list */}
      <DialogContent sx={{ px: 2.5, py: 0, maxHeight: 380, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: "#f97316" }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1, color: "#9ca3af" }}>
            <FolderOutlinedIcon sx={{ fontSize: 40, opacity: 0.35 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {assets.length === 0 ? "No assets uploaded yet" : "No files match your search"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pb: 1 }}>
            {filtered.map((asset) => {
              const isSelected = selected?.name === asset.name;
              return (
                <Paper
                  key={asset.name}
                  elevation={0}
                  onClick={() => setSelected(isSelected ? null : asset)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: "12px",
                    cursor: "pointer",
                    border: `1.5px solid ${isSelected ? "rgba(249,115,22,0.50)" : "rgba(255,132,76,0.10)"}`,
                    backgroundColor: isSelected ? "rgba(255,241,233,0.96)" : "rgba(255,255,255,0.82)",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: "rgba(249,115,22,0.35)",
                      backgroundColor: "rgba(255,247,241,0.92)",
                    },
                  }}
                >
                  {/* Thumb */}
                  <AssetThumb asset={asset} size={28} />

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#202531",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {asset.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                      {formatSize(asset.size)}
                      {asset.lastModified && ` · ${new Date(asset.lastModified).toLocaleDateString()}`}
                    </Typography>
                  </Box>

                  {/* Selected check */}
                  {isSelected && (
                    <CheckCircleOutlinedIcon sx={{ color: "#f97316", fontSize: 20, flexShrink: 0 }} />
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, px: 2.5, py: 1, borderTop: "1px solid rgba(255,132,76,0.08)" }}>
          <Button
            size="small"
            disabled={page === 0 || loading}
            onClick={() => fetchAssets(page - 1)}
            sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
          >
            Previous
          </Button>
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            Page {page + 1} of {Math.ceil(totalCount / PAGE_SIZE)}
          </Typography>
          <Button
            size="small"
            disabled={page >= Math.ceil(totalCount / PAGE_SIZE) - 1 || loading}
            onClick={() => fetchAssets(page + 1)}
            sx={{ color: "#f97316", textTransform: "none", fontWeight: 600 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Selected URL preview */}
      {selected && (
        <Box
          sx={{
            mx: 2.5,
            mb: 1.5,
            mt: 0.5,
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "rgba(255,241,233,0.6)",
            border: "1px solid rgba(249,115,22,0.18)",
          }}
        >
          <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 0.25, fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            URL to be inserted
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#ea580c",
              fontFamily: "monospace",
              fontSize: "0.78rem",
              wordBreak: "break-all",
              display: "block",
            }}
          >
            {selected.url}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected}
          onClick={handleConfirm}
          sx={{
            background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
            boxShadow: "0 14px 30px rgba(255,139,71,0.28)",
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 700,
            px: 2.5,
            "&:hover": { background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)" },
            "&:disabled": { background: "linear-gradient(180deg, #ffd4b5 0%, #ffb880 100%)", boxShadow: "none", color: "rgba(255,255,255,0.7)" },
          }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetPickerDialog;
